import ColorPicker from '@/components/commons/color-picker';
import { ElementProps } from '@/components/sections/builder/types';
import { ProductGridElementDataType } from '@/components/sections/builder/elements/body/collection-product-grid/type';
import InputSlider from '@/components/sections/builder/settings/input-slider';
import PaddingSetting from '@/components/sections/builder/settings/padding-setting';

import { useBuilderStore } from '@/stores/builder';
import { ColorResult } from 'react-color';
import { shallow } from 'zustand/shallow';
import SchemeSetting from '@/components/sections/builder/settings/scheme-setting';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import InputSelect from '@/components/sections/builder/settings/input-select';

export default function ElementSetting({ element }: ElementProps) {
  const builder = useBuilderStore(
    (state) => ({
      setElementData: state.setElementData,
      clearSelected: state.clearSelected,
    }),
    shallow
  );

  const data = element.data as ProductGridElementDataType;

  const onChangeColor = (
    color: ColorResult & {
      source: 'rgb' | 'hex' | 'hsl';
    }
  ) => {
    if (color.source === 'rgb') {
      builder.setElementData(element.id, {
        overlay: `rgba(${color.rgb.r},${color.rgb.g},${color.rgb.b},${color.rgb.a})`,
      });
    } else {
      builder.setElementData(element.id, { overlay: color.hex });
    }
  };

  return (
    <div>
      <h1 className='mb-3 text-sm font-bold'>Layout Settings</h1>
      <div className='text-xs'>Product per page </div>
      <InputSlider
        value={data.limit || 0}
        max={25}
        onChange={(val) =>
          builder.setElementData(element.id, {
            limit: val,
          })
        }
      />
      <div className='mt-4 text-xs '>Number of columns on desktop</div>
      <InputSlider
        value={data.totalCol || 1}
        min={1}
        max={5}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalCol: val,
          })
        }
      />
      <div className='mt-4 text-xs '>Number of columns on mobile</div>
      <InputSlider
        value={data.totalColMobile || 1}
        min={1}
        max={3}
        onChange={(val) =>
          builder.setElementData(element.id, {
            totalColMobile: val,
          })
        }
      />
      <div className='mt-4 flex items-center gap-2 '>
        <label>Enable quick add button</label>
        <Switch
          className='ml-auto'
          checked={data.enableQuickAddButton}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableQuickAddButton: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Product Card Settings</h1>
      <div className='mb-2 grid grid-cols-2 items-center gap-2'>
        <label className='text-xs'>Image ratio</label>
        <InputSelect
          defaultValue={data.imageRatio}
          options={[
            { label: 'Adapt to image', value: 'adaptToImage' },
            { label: 'Portrait', value: 'portrait' },
            { label: 'Square', value: 'square' },
          ]}
          onChange={(v) =>
            builder.setElementData(element.id, {
              imageRatio: v,
            })
          }
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Show image</label>
        <Switch
          className='ml-auto'
          checked={data.showImage}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showImage: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Show second image on hover</label>
        <Switch
          className='ml-auto'
          checked={data.showSecondImageOnHover}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showSecondImageOnHover: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Show shadow</label>
        <Switch
          className='ml-auto'
          checked={data.showShadow}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showShadow: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Show category</label>
        <Switch
          className='ml-auto'
          checked={data.showCategory}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showCategory: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label htmlFor='showTotalProduct'>Show total product</label>
        <Switch
          id='showTotalProduct'
          aria-label='Select'
          className='ml-auto'
          checked={data.showProductNameCount ?? true}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              showProductNameCount: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <h1 className='mb-2 mt-4 font-bold'>Filtering and sorting</h1>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Enable filtering</label>
        <Switch
          className='ml-auto'
          checked={data.enableFilter}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableFilter: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      <div className='mt-4 flex items-center gap-2 '>
        <label>Enable sorting</label>
        <Switch
          className='ml-auto'
          checked={data.enableSort}
          onCheckedChange={(checked) => {
            builder.setElementData(element.id, {
              enableSort: !!checked,
            });
          }}
          aria-readonly
        />
      </div>
      {data.enableFilter || data.enableSort ? (
        <div className='my-4 grid grid-cols-2 items-center gap-2'>
          <label className='text-xs'>Desktop filter layout</label>
          <InputSelect
            defaultValue={data.filterLayout || 'horizontal'}
            options={[
              { label: 'Horizontal', value: 'horizontal' },
              { label: 'Vertical', value: 'vertical' },
              { label: 'Drawer', value: 'drawer' },
            ]}
            onChange={(v) =>
              builder.setElementData(element.id, {
                filterLayout: v,
              })
            }
          />
        </div>
      ) : null}
      {data.enableFilter && (
        <>
          <div className='mt-4 flex items-center gap-2 '>
            <label>Show filter category</label>
            <Switch
              className='ml-auto'
              checked={data.showFilterCategory ?? true}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showFilterCategory: !!checked,
                });
              }}
              aria-readonly
            />
          </div>
          <div className='mt-4 flex items-center gap-2 '>
            <label>Show filter availability</label>
            <Switch
              className='ml-auto'
              checked={data.showFilterAvailability ?? true}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showFilterAvailability: !!checked,
                });
              }}
              aria-readonly
            />
          </div>
          <div className='mt-4 flex items-center gap-2 '>
            <label>Show filter price</label>
            <Switch
              className='ml-auto'
              checked={data.showFilterPrice ?? true}
              onCheckedChange={(checked) => {
                builder.setElementData(element.id, {
                  showFilterPrice: !!checked,
                });
              }}
              aria-readonly
            />
          </div>
        </>
      )}

      <Separator className='my-4' />
      <h1 className='mb-3 text-sm font-bold'>Style</h1>
      <div className='mt-4 '>Color scheme</div>
      <SchemeSetting
        value={data.colorScheme}
        onChange={(theme) => {
          builder.setElementData(element.id, {
            colorScheme: theme,
          });
        }}
      />
      <div className='mt-4 '>Overlay</div>
      <ColorPicker
        color={data.overlay}
        inputColor={data.overlay}
        onChangeColorComplete={(color) => onChangeColor(color)}
        onChangeInputColor={(e) => builder.setElementData(element.id, { overlay: e.target.value })}
      />
      <PaddingSetting
        value={data.padding}
        onChange={(v) => builder.setElementData(element.id, { padding: v })}
      />
    </div>
  );
}
